import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth';
import { isNative, isIOS } from '@/lib/capacitor';
import { checkPremiumEntitlement, isRevenueCatAvailable } from '@/lib/revenuecat';

export type SubscriptionStatus = 'none' | 'active' | 'trialing' | 'past_due' | 'canceled' | 'unpaid';

export interface SubscriptionDetails {
  status: SubscriptionStatus;
  plan: {
    name: string;
    interval: 'month' | 'year';
    amount: number;
    currency: string;
  } | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  trialEnd: string | null;
  paymentMethod: {
    type: string;
    last4: string | null;
    brand: string | null;
  } | null;
}

export type PremiumSource = 'stripe' | 'beta_code' | 'promo_code' | 'apple' | 'google' | 'admin_grant' | null;

export interface SubscriptionState {
  tier: 'free' | 'premium';
  status: SubscriptionStatus;
  periodEnd: string | null;
  isPremium: boolean;
  isBetaTester: boolean;
  betaActivatedAt: string | null;
  isSharedPremium: boolean;
  sharedFromId: string | null;
  premiumExpiresAt: string | null;
  premiumSource: PremiumSource;
  isLoading: boolean;
}

// Basic subscription info from DB (cached, fast)
export function useSubscription() {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['subscription', user?.id],
    queryFn: async (): Promise<SubscriptionState> => {
      if (!user?.id) {
        return {
          tier: 'free',
          status: 'none',
          periodEnd: null,
          isPremium: false,
          isBetaTester: false,
          betaActivatedAt: null,
          isSharedPremium: false,
          sharedFromId: null,
          premiumExpiresAt: null,
          premiumSource: null,
          isLoading: false,
        };
      }

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('subscription_tier, subscription_status, premium_shared_from, is_beta_tester, beta_granted_at, free_premium_until, premium_expires_at, premium_source')
        .eq('id', user.id)
        .single();

      if (error || !profile) {
        console.error('Error fetching subscription:', error);
        return {
          tier: 'free',
          status: 'none',
          periodEnd: null,
          isPremium: false,
          isBetaTester: false,
          betaActivatedAt: null,
          isSharedPremium: false,
          sharedFromId: null,
          premiumExpiresAt: null,
          premiumSource: null,
          isLoading: false,
        };
      }

      const tier = (profile.subscription_tier || 'free') as 'free' | 'premium';
      const status = (profile.subscription_status || 'none') as SubscriptionStatus;
      const sharedFromId = profile.premium_shared_from as string | null;
      const isSharedPremium = !!sharedFromId;
      const isBetaTester = profile.is_beta_tester || false;
      const betaActivatedAt = profile.beta_granted_at || null;
      const premiumExpiresAt = (profile.premium_expires_at as string | null) || null;
      const premiumSource = (profile.premium_source as PremiumSource) || null;

      // User has premium if:
      // 1. subscription_tier is 'premium', OR
      // 2. They have shared premium from a partner (premium_shared_from is set)
      let hasPremium = tier === 'premium' || isSharedPremium;

      // On native platforms, also check RevenueCat entitlements directly
      // This ensures premium works immediately after purchase, even before webhook is processed
      let iapPremium = false;
      if (isNative && isRevenueCatAvailable() && !hasPremium) {
        try {
          iapPremium = await checkPremiumEntitlement();
          if (iapPremium) {
            console.log('Premium detected via RevenueCat entitlement (webhook may still be pending)');
            hasPremium = true;
          }
        } catch (error) {
          console.error('Error checking RevenueCat entitlement:', error);
        }
      }

      return {
        tier: hasPremium ? 'premium' : 'free',
        status: iapPremium && status === 'none' ? 'active' : status,
        periodEnd: profile.free_premium_until || null,
        isPremium: hasPremium,
        isBetaTester,
        betaActivatedAt,
        isSharedPremium,
        sharedFromId,
        premiumExpiresAt,
        premiumSource: iapPremium && !premiumSource ? (isIOS ? 'apple' : 'google') : premiumSource,
        isLoading: false,
      };
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000, // 30 seconds
  });

  return {
    ...data,
    isLoading,
    tier: data?.tier || 'free',
    status: data?.status || 'none',
    periodEnd: data?.periodEnd || null,
    isPremium: data?.isPremium || false,
    isBetaTester: data?.isBetaTester || false,
    betaActivatedAt: data?.betaActivatedAt || null,
    isSharedPremium: data?.isSharedPremium || false,
    sharedFromId: data?.sharedFromId || null,
    premiumExpiresAt: data?.premiumExpiresAt || null,
    premiumSource: data?.premiumSource || null,
  };
}

// Detailed subscription info from Stripe API (slower, more details)
export function useSubscriptionDetails() {
  const { user, session } = useAuth();

  return useQuery({
    queryKey: ['subscription-details', user?.id],
    queryFn: async (): Promise<SubscriptionDetails> => {
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase.functions.invoke('stripe-subscription-details', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('Error fetching subscription details:', error);
        throw error;
      }

      return data as SubscriptionDetails;
    },
    enabled: !!user?.id && !!session?.access_token,
    staleTime: 60 * 1000, // 1 minute
  });
}

// Create Stripe Checkout session
export function useCreateCheckout() {
  const { session } = useAuth();

  return useMutation({
    mutationFn: async (params: {
      priceId: string;
      successUrl?: string;
      cancelUrl?: string;
    }) => {
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase.functions.invoke('stripe-checkout', {
        body: {
          priceId: params.priceId,
          successUrl: params.successUrl || `${window.location.origin}/subscription/success`,
          cancelUrl: params.cancelUrl || `${window.location.origin}/profile`,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('Error creating checkout:', error);
        throw error;
      }

      return data as { sessionId: string; url: string };
    },
  });
}

// Create Stripe Customer Portal session
export function useCreatePortalSession() {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (returnUrl?: string) => {
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase.functions.invoke('stripe-portal', {
        body: {
          returnUrl: returnUrl || `${window.location.origin}/profile`,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('Error creating portal session:', error);
        throw error;
      }

      return data as { url: string };
    },
    onSuccess: () => {
      // Invalidate subscription queries after portal visit
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-details'] });
    },
  });
}

// Helper to format price
export function formatPrice(amount: number, currency: string = 'eur'): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount);
}

// Helper to format date
export function formatSubscriptionDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

// Status labels in German
export const subscriptionStatusLabels: Record<SubscriptionStatus, string> = {
  none: 'Kein Abo',
  active: 'Aktiv',
  trialing: 'Testphase',
  past_due: 'Zahlung ausstehend',
  canceled: 'Gekündigt',
  unpaid: 'Unbezahlt',
};
